import AHttpService from '@d-services/a-http-service/a-http-service';
import { addObjectToFormData } from '@d-shared/forms/forms';
import DropdownOptionsHelper from '@d-helpers/dropdown-options-helper';
import URLS from '@d-shared/urls/urls';

const JUST_ADDED_BOOK_TO_REVIEW_KEY = 'book-added-to-review';

class LibraryService extends AHttpService {
  async acceptDirectSubmission(bookSlug) {
    const url = URLS.api.library.acceptDirectSubmission.replace('<book-slug>', bookSlug);
    const response = await this._http.post(url, null);

    return response.data;
  }

  async cancelReview(bookSlug, cancelReviewData) {
    const formData = new FormData();
    addObjectToFormData(cancelReviewData, formData);

    const url = URLS.api.library.cancelReview.replace('<book-slug>', bookSlug);
    const response = await this._http.post(url, formData);
    return response.data;
  }

  async cancelSkipBook(bookSlug) {
    const url = URLS.api.library.cancelSkipBook.replace('<book-slug>', bookSlug);
    const response = await this._http.delete(url);
    return response.data;
  }

  async createReview(bookSlug) {
    const data = {
      book_id: bookSlug,
    };
    const formData = new FormData();
    addObjectToFormData(data, formData);

    const response = await this._http.post(URLS.api.library.reviews, formData);
    return response.data;
  }

  async createUserReview(reviewData) {
    const formData = new FormData();
    addObjectToFormData(reviewData, formData);
    const response = await this._http.post(URLS.api.library.userReviews, formData);
    return response.data;
  }

  async deleteUserReview(slug) {
    const url = URLS.api.library.userReview.replace('<slug>', slug);
    const response = await this._http.delete(url);
    return response.data;
  }

  async denyDirectSubmission(bookSlug) {
    const url = URLS.api.library.denyDirectSubmission.replace('<book-slug>', bookSlug);
    const response = await this._http.post(url, null);
    return response.data;
  }

  async getBookFormats() {
    try {
      const response = await this._http.get(URLS.api.library.formats);
      const formatOptions = response.data.formats;
      return DropdownOptionsHelper.getOptionsListFromStrings(formatOptions);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        return null;
      }

      throw error;
    }
  }

  async getCounters() {
    const response = await this._http.get(URLS.api.library.counters);
    return response.data;
  }

  async getCanceledReviews() {
    const response = await this._http.get(URLS.api.library.canceledReviews);
    return response.data.reviews;
  }

  async getDidNotFinishReasons() {
    try {
      const response = await this._http.get(URLS.api.library.didNotFinishReasons);
      const didNotFinishOptions = response.data.book_report_reasons;
      return DropdownOptionsHelper.getOptionsListFromStrings(didNotFinishOptions);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        return null;
      }

      throw error;
    }
  }

  async getDirectSubmissions() {
    const response = await this._http.get(URLS.api.library.directSubmissions);
    return response.data.reviews;
  }

  async getInProgressReviews() {
    const response = await this._http.get(URLS.api.library.inProgressReviews);
    return response.data.reviews;
  }

  getJustAddedBookForReview(shouldKeep = false) {
    const bookSlug = localStorage.getItem(JUST_ADDED_BOOK_TO_REVIEW_KEY);

    if (!shouldKeep) {
      localStorage.removeItem(JUST_ADDED_BOOK_TO_REVIEW_KEY);
    }

    return bookSlug;
  }

  async getReadingGoal() {
    const response = await this._http.get(URLS.api.library.readingGoal);
    return response.data;
  }

  async getReportSpamReasons() {
    try {
      const response = await this._http.get(URLS.api.library.reportSpamReasons);
      const reportSpamOptions = response.data.book_report_reasons;
      return DropdownOptionsHelper.getOptionsListFromStrings(reportSpamOptions);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        return null;
      }

      throw error;
    }
  }

  async getUserReview(slug) {
    const url = URLS.api.library.userReview.replace('<slug>', slug);
    const response = await this._http.get(url);
    return response.data;
  }

  async publishReview(bookSlug) {
    const url = URLS.api.library.publishReview.replace('<book-slug>', bookSlug);
    const response = await this._http.post(url, null);
    return response.data;
  }

  async publishUserReview(slug) {
    const url = URLS.api.library.publishUserReview.replace('<slug>', slug);
    const response = await this._http.post(url, null);
    return response.data;
  }

  async reportSpam(bookReportData) {
    const formData = new FormData();
    addObjectToFormData(bookReportData, formData);

    const response = await this._http.post(URLS.api.library.reportSpam, formData);
    return response.data;
  }

  async restoreDirectSubmission(bookSlug) {
    const url = URLS.api.library.restoreDirectSubmission.replace('<book-slug>', bookSlug);
    const response = await this._http.post(url, null);
    return response.data;
  }

  saveJustAddedBookForReview(bookSlug) {
    localStorage.setItem(JUST_ADDED_BOOK_TO_REVIEW_KEY, bookSlug);
  }

  async saveReadingGoal(numberOfBooks) {
    const data = {
      reading_goal: numberOfBooks,
    };
    const formData = new FormData();
    addObjectToFormData(data, formData);
    const response = await this._http.put(URLS.api.library.readingGoal, formData);
    return response.data;
  }

  async saveReview(bookSlug, reviewData) {
    const formData = new FormData();
    addObjectToFormData(reviewData, formData);

    const url = URLS.api.library.review.replace('<book-slug>', bookSlug);
    const response = await this._http.patch(url, formData);
    return response.data;
  }

  async saveUserReview(slug, reviewData) {
    const formData = new FormData();
    addObjectToFormData(reviewData, formData);

    const url = URLS.api.library.userReview.replace('<slug>', slug);
    const response = await this._http.patch(url, formData);
    return response.data;
  }

  async skipBook(bookSlug) {
    const data = {
      id: bookSlug,
    };
    const formData = new FormData();
    addObjectToFormData(data, formData);

    const response = await this._http.post(URLS.api.library.skipBook, formData);
    return response.data;
  }
}

export default new LibraryService();
